import Phaser from "phaser";
import { Palette } from "./palette";
import { Tex } from "./textures";

export interface ButtonOpts {
  width?: number;
  height?: number;
  fill?: number;
  fontSize?: string;
  enabled?: boolean;
  /** Image-button: texture drawn instead of the rect + text (its own art carries
   *  the label). `hoverTexture` swaps in on pointer-over. Fitted to `width`,
   *  aspect preserved so the baked-in label never squishes. */
  texture?: string;
  hoverTexture?: string;
}

/** A rectangular text button. Returns the container so callers can reposition
 *  or disable it. Disabled buttons render dim and ignore input. When `texture`
 *  is given it becomes an image button (see ButtonOpts). */
export function makeButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  onClick: () => void,
  opts: ButtonOpts = {},
): Phaser.GameObjects.Container {
  const w = opts.width ?? 220;
  const h = opts.height ?? 48;
  const enabled = opts.enabled ?? true;
  const fill = opts.fill ?? Palette.panel;

  if (opts.texture && scene.textures.exists(opts.texture)) {
    return makeImageButton(scene, x, y, w, onClick, enabled, opts.texture, opts.hoverTexture);
  }

  const bg = scene.add.rectangle(0, 0, w, h, enabled ? fill : Palette.panel, 1);
  bg.setStrokeStyle(2, enabled ? Palette.panelEdge : 0x23262e);
  const txt = scene.add
    .text(0, 0, label, {
      color: enabled ? Palette.text : Palette.textDim,
      fontSize: opts.fontSize ?? "18px",
      fontFamily: "monospace",
    })
    .setOrigin(0.5);

  const container = scene.add.container(x, y, [bg, txt]);
  container.setSize(w, h);

  if (enabled) {
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerover", () => bg.setFillStyle(Palette.panelEdge, 1));
    bg.on("pointerout", () => bg.setFillStyle(fill, 1));
    bg.on("pointerdown", onClick);
  }
  return container;
}

/** An image button: the artwork is the whole button (label baked in). Fitted to
 *  `width` with aspect preserved; hover swaps to `hoverTexture` (the art lights
 *  up) with no resize. */
function makeImageButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  onClick: () => void,
  enabled: boolean,
  texture: string,
  hoverTexture?: string,
): Phaser.GameObjects.Container {
  const img = scene.add.image(0, 0, texture);
  img.setScale(width / img.width); // fit to width, keep aspect (no squished label)
  img.setAlpha(enabled ? 1 : 0.4);

  const container = scene.add.container(x, y, [img]);
  container.setSize(img.displayWidth, img.displayHeight);

  if (enabled) {
    const hover = hoverTexture && scene.textures.exists(hoverTexture) ? hoverTexture : texture;
    img.setInteractive({ useHandCursor: true });
    img.on("pointerover", () => img.setTexture(hover)); // light up (brighter art), no resize
    img.on("pointerout", () => img.setTexture(texture));
    img.on("pointerdown", onClick);
  }
  return container;
}

/** Row of up to `max` stars, `filled` of them lit. */
export function starRow(
  scene: Phaser.Scene,
  x: number,
  y: number,
  filled: number,
  max = 3,
  scale = 1,
): Phaser.GameObjects.Container {
  // STAR_PX is the star HEIGHT at scale 1; width follows the art's aspect (stars are
  // tightly cropped, not square), and stars are spaced by their real width plus a gap.
  const STAR_PX = 34;
  const h = STAR_PX * scale;
  const items: Phaser.GameObjects.Image[] = [];
  for (let i = 0; i < max; i++) {
    const key = i < filled ? Tex.star : Tex.starEmpty;
    const s = scene.add.image(0, 0, key);
    s.setScale(h / s.height); // height = h, aspect preserved
    items.push(s);
  }
  const gap = items[0].displayWidth + 6 * scale;
  items.forEach((s, i) => s.setX(i * gap - ((max - 1) * gap) / 2));
  return scene.add.container(x, y, items);
}
